import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useEquipmentScope } from './equipmentScope'
import { Plus } from 'lucide-react'
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { EquipmentGroup } from '@/types/equipment'
import { useFacilityLocations } from '@/hooks/useFacility'
import { floorOptions, formatPlace, indexRooms } from '@/lib/facilityLocation'
import { RoomPicker } from './facility/RoomPicker'
import { FloorPicker } from './facility/FloorPicker'

/**
 * An existing equipment type the unit can be added under. Carries what the unit
 * inherits by being of this type: its group, and whether it is serviced at all.
 */
export type EquipmentTypeOption = { name: string; group: string; maintained: boolean }

export type NewEquipmentInput = {
  equipmentId: string
  equipmentType: string
  group: EquipmentGroup
  model: string
  serial: string
  vendor: string
  installed: number
  /** The room it stands in (z8nrz7fw25); null: in none yet. Never set for a room. */
  roomId: string | null
  /** Rooms only: the floor it is on; null: on none yet. */
  floorId: string | null
  /** Where it is, as the chosen room or floor reads ("NYP › Building 1 › …"), for the asset tag. */
  place: string | null
}

const EMPTY = {
  equipmentId: '',
  equipmentType: '',
  model: '',
  serial: '',
  vendor: '',
  installed: '',
}

const CURRENT_YEAR = new Date().getFullYear()
/** Selectable install years — next year down to 1990. */
const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR + 1 - 1990 + 1 }, (_, i) => CURRENT_YEAR + 1 - i)

function Labeled({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className='space-y-1'>
      <label className='text-xs font-medium text-gray-600'>{label}</label>
      {children}
    </div>
  )
}

export function AddEquipmentModal({
  open,
  onClose,
  onAdd,
  types,
}: {
  open: boolean
  onClose: () => void
  onAdd: (data: NewEquipmentInput) => void
  /** Existing equipment types to add the unit under; the group comes from the type. */
  types: EquipmentTypeOption[]
}) {
  const { copy, variant } = useEquipmentScope()
  const isRoom = variant === 'room'
  const [form, setForm] = useState(EMPTY)
  const [roomId, setRoomId] = useState<string | null>(null)
  const [floorId, setFloorId] = useState<string | null>(null)
  const locations = useFacilityLocations(open)

  useEffect(() => {
    if (!open) return
    setForm(EMPTY)
    setRoomId(null)
    setFloorId(null)
  }, [open])

  // Where it will read as: the place is the room (equipment) or the floor
  // (a room), never typed — a free-text location repeated the room and drifted
  // from it (NYP feedback, 2026-10-29).
  const place = useMemo(() => {
    if (!locations.data) return null
    if (isRoom) return floorOptions(locations.data).find((f) => f.value === floorId)?.label ?? null
    const at = roomId ? indexRooms(locations.data).get(roomId) : undefined
    return at ? formatPlace(at) : null
  }, [locations.data, isRoom, floorId, roomId])

  const set = (key: keyof typeof EMPTY, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  // Nothing below the type picker is chosen directly — the group and the
  // maintenance behaviour are both inherited from the selected type.
  const chosenType = useMemo(
    () => types.find((t) => t.name === form.equipmentType) ?? null,
    [types, form.equipmentType],
  )
  const group = chosenType?.group ?? ''

  const canSubmit = form.equipmentId.trim() !== '' && form.equipmentType !== '' && group !== ''

  const handleAdd = () => {
    if (!canSubmit) return
    onAdd({
      equipmentId: form.equipmentId.trim(),
      equipmentType: form.equipmentType,
      group,
      // A room has no model, serial or vendor.
      model: isRoom ? '' : form.model.trim(),
      serial: isRoom ? '' : form.serial.trim(),
      vendor: isRoom ? '' : form.vendor.trim(),
      installed: Number(form.installed) || CURRENT_YEAR,
      roomId: isRoom ? null : roomId,
      floorId: isRoom ? floorId : null,
      place,
    })
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='sm:max-w-lg'>
        <div className='flex items-start gap-3'>
          <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary'>
            <Plus className='h-5 w-5' />
          </div>
          <div>
            <h2 className='text-base font-semibold text-gray-900'>{copy.addUnit}</h2>
            <p className='mt-0.5 text-xs text-muted-foreground'>
              {chosenType && !chosenType.maintained
                ? copy.registerUnscheduled
                : copy.registerScheduled}
            </p>
          </div>
        </div>

        {types.length === 0 && (
          <div className='mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800'>
            No {copy.unitLower} types yet — add one with “{copy.addType}” first.
          </div>
        )}

        {/* Type first: it decides the group, the maintenance behaviour and the
            schedule the unit inherits, so answering it first means the rest of
            the form is already describing something. Naming the unit before
            knowing what it is was the wrong way round. */}
        <div className='mt-4 grid grid-cols-2 gap-3'>
          <Labeled label={copy.type}>
            <Select
              value={form.equipmentType}
              onValueChange={(v) => set('equipmentType', v)}
              disabled={types.length === 0}
            >
              <SelectTrigger className='w-full'>
                <SelectValue placeholder='Select a type' />
              </SelectTrigger>
              <SelectContent>
                {types.map((t) => (
                  <SelectItem key={t.name} value={t.name}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Labeled>
          <Labeled label={copy.unitId}>
            <Input
              value={form.equipmentId}
              onChange={(e) => set('equipmentId', e.target.value)}
              placeholder={copy.unitExample}
            />
          </Labeled>
          <Labeled label={copy.group}>
            <div className='flex h-9 items-center rounded-md border border-gray-200 bg-gray-50/60 px-3 text-sm text-gray-700'>
              {group || <span className='text-gray-400'>Set by the {copy.unitLower} type</span>}
            </div>
          </Labeled>
          {/* Read back the same way the group is: inherited from the type, not
              a choice here. Otherwise the modal promises a schedule this unit
              will never get. */}
          <Labeled label={copy.cycleLabel}>
            <div className='flex h-9 items-center rounded-md border border-gray-200 bg-gray-50/60 px-3 text-sm text-gray-700'>
              {chosenType ? (
                chosenType.maintained ? (
                  'Serviced on a cycle'
                ) : (
                  <span className='text-gray-500'>{copy.notOnCycle}</span>
                )
              ) : (
                <span className='text-gray-400'>Set by the {copy.unitLower} type</span>
              )}
            </div>
          </Labeled>
          <Labeled label='Installed (year)'>
            <Select value={form.installed} onValueChange={(v) => set('installed', v)}>
              <SelectTrigger className='w-full'>
                <SelectValue placeholder='Select a year' />
              </SelectTrigger>
              <SelectContent className='max-h-60'>
                {YEAR_OPTIONS.map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Labeled>
          {/* A room has no model, serial or vendor (NYP feedback, 2026-10-29). */}
          {!isRoom && (
            <>
              <Labeled label='Model'>
                <Input value={form.model} onChange={(e) => set('model', e.target.value)} placeholder='Model' />
              </Labeled>
              <Labeled label='Serial'>
                <Input value={form.serial} onChange={(e) => set('serial', e.target.value)} placeholder='SN-…' />
              </Labeled>
              <Labeled label='Vendor'>
                <Input value={form.vendor} onChange={(e) => set('vendor', e.target.value)} placeholder='Vendor' />
              </Labeled>
            </>
          )}
        </div>
        {/* Where it is: equipment by its room, a room by its floor. */}
        <div className='mt-3'>
          {isRoom ? (
            <Labeled label='Floor'>
              <FloorPicker value={floorId} onChange={setFloorId} />
            </Labeled>
          ) : (
            <Labeled label='Room'>
              <RoomPicker value={roomId} onChange={setRoomId} />
            </Labeled>
          )}
        </div>

        <DialogFooter className='mt-5'>
          <Button variant='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleAdd} disabled={!canSubmit}>
            {copy.addUnit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
